// Who the lead popup is allowed to interrupt, and what it already knows about them.
//
// Kept in localStorage, not sessionStorage. The old key died with the tab, so a
// customer who dismissed the popup was asked again on their very next visit,
// forever. Measured 16 Aug - 15 Sep: of 2,511 popup leads, 1,512 had never run
// a single search and produced 3 paid bookings between them (0.20%), while the
// 175 who had searched AND seen results converted at 5.14%. So intent, not a
// timer, is what earns the interruption.

const INTENT_KEY = "sukutoSearchIntent";
const SUPPRESS_KEY = "sukutoLeadPopup";

const DISMISS_DAYS = 45;
// A search stops describing what someone wants after a few hours.
const INTENT_TTL_MS = 6 * 60 * 60 * 1000;

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / storage disabled. Failing closed here only means the
    // popup stays quiet, which is the safe direction.
  }
};

// A search that returned nothing is not intent — it is a dead end, and those
// leads converted at 1.10% against 5.14% for searches that found vehicles.
export const markSearchIntent = ({ city, startDate, endDate, startTime, endTime, resultCount } = {}) => {
  if (!resultCount) return;
  write(INTENT_KEY, {
    ts: Date.now(),
    city: city || "",
    startDate: startDate || "",
    endDate: endDate || "",
    startTime: startTime || "",
    endTime: endTime || "",
  });
};

export const readSearchIntent = () => {
  const intent = read(INTENT_KEY);
  if (!intent?.ts || Date.now() - intent.ts > INTENT_TTL_MS) return null;
  return intent;
};

// permanent = they gave us the number; there is never a reason to ask again.
export const suppressLeadPopup = (permanent = false) =>
  write(SUPPRESS_KEY, { ts: Date.now(), permanent: Boolean(permanent) });

export const isLeadPopupSuppressed = () => {
  const s = read(SUPPRESS_KEY);
  if (!s) return false;
  if (s.permanent) return true;
  return Date.now() - Number(s.ts || 0) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

// The search form stores date as YYYY-MM-DD and time as HH:mm, and builds
// `${date}T${time}` to get a local Date (SearchFormMobile:162) - same here, so a
// malformed pair fails as Invalid Date rather than silently shifting the day.
// The shape is checked before parsing because Date is far too forgiving: a
// stray "20 Sep" parses to "Thu 20 Sep 2001" rather than Invalid Date, so an
// isNaN guard alone would put a confidently wrong year in front of an agent.
// Showing nothing beats showing the wrong pickup date.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME = /^\d{2}:\d{2}$/;

const toDate = (date, time) => {
  if (!ISO_DATE.test(String(date || ""))) return null;
  const clock = ISO_TIME.test(String(time || "")) ? time : "00:00";
  const d = new Date(`${date}T${clock}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Hand-rolled rather than toLocaleDateString: that depends on the runtime's ICU
// data, so the same lead could read "18 Sept" in one environment and "18 Sep" in
// another. A label that lands on a ticket should be byte-identical everywhere.
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fmtDay = (d) => {
  const base = `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  // Year only when it is not the current one — noise on the near-term bookings
  // that are the overwhelming majority, but essential across a new year.
  return d.getFullYear() === new Date().getFullYear() ? base : `${base} ${d.getFullYear()}`;
};

const fmtTime = (d) => {
  const h = d.getHours();
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(d.getMinutes()).padStart(2, "0")} ${suffix}`;
};

const fmtGap = (start, end) => {
  const totalHours = Math.round((end - start) / 3600000);
  if (end <= start) return "";
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const parts = [];
  if (days) parts.push(`${days} day${days > 1 ? "s" : ""}`);
  if (hours) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
  return parts.join(" ") || "under an hour";
};

// What the agent sees on the ticket. Was "Goa · 2026-09-18-2026-09-19", where
// three hyphens did two different jobs and neither the time nor the length of
// the hire was there at all - on an hourly rental that is most of the question.
// Now: "Goa · 1 day 9 hrs · Fri 18 Sep, 9:00 AM → Sat 19 Sep, 6:00 PM".
// Structured copy of the search for the lead WhatsApp; empty unless every part
// is well-formed, so the server never has to guess a date.
export const leadIntentFields = (intent = readSearchIntent()) => {
  if (!intent?.city) return {};
  const whole = (d, t) => ISO_DATE.test(String(d || "")) && ISO_TIME.test(String(t || ""));
  if (!whole(intent.startDate, intent.startTime) || !whole(intent.endDate, intent.endTime)) return {};
  return {
    intentCity: intent.city,
    intentStart: `${intent.startDate} ${intent.startTime}`,
    intentEnd: `${intent.endDate} ${intent.endTime}`,
  };
};

export const describeIntent = (intent) => {
  if (!intent) return "";

  const start = toDate(intent.startDate, intent.startTime);
  const end = toDate(intent.endDate, intent.endTime);

  let when = "";
  if (start && end) {
    const sameDay = intent.startDate === intent.endDate;
    const hasStartTime = ISO_TIME.test(String(intent.startTime || ""));
    const hasEndTime = ISO_TIME.test(String(intent.endTime || ""));
    const from = hasStartTime ? `${fmtDay(start)}, ${fmtTime(start)}` : fmtDay(start);
    // A same-day hire does not need the date printed twice.
    const to = sameDay && hasEndTime
      ? fmtTime(end)
      : (hasEndTime ? `${fmtDay(end)}, ${fmtTime(end)}` : fmtDay(end));
    when = `${from} → ${to}`;
  } else if (start) {
    when = ISO_TIME.test(String(intent.startTime || "")) ? `${fmtDay(start)}, ${fmtTime(start)}` : fmtDay(start);
  }

  // Duration ahead of the window (Chandan, 2026-09-16): how long the hire is
  // is the fact an agent scans for, and it reads before the dates, not after.
  const howLong = start && end ? fmtGap(start, end) : "";
  return [intent.city, howLong, when].filter(Boolean).join(" · ");
};
